/**
 * @fileoverview Authentication Hook
 *
 * Core authentication hook providing user state and auth methods.
 * Now reads from the global AuthContext for stable auth state across navigation.
 * 
 * IMPORTANT: When admin impersonation is active, `user` returns a synthetic
 * user object with the impersonated user's ID. Use `realUser` to get the
 * actual authenticated admin user.
 *
 * @module hooks/useAuth
 */

import { useAuthContext } from '@/contexts/AuthContext';
import { useContext } from 'react';
import type { User } from '@supabase/supabase-js';
import { ImpersonationContext, type ImpersonationContextType } from '@/contexts/ImpersonationContext';

/**
 * Authentication hook for user session management.
 *
 * Handles:
 * - Session persistence across page reloads
 * - Real-time auth state changes (login, logout, token refresh)
 * - Email/password authentication
 * - Google OAuth flow
 * - Password reset flow
 * - Admin impersonation (returns impersonated user's ID when active)
 *
 * Now uses global AuthContext to prevent "flash of empty state" when
 * navigating between pages. Auth state is initialized once at app startup.
 *
 * @returns {Object} Auth state and methods
 * @property {User | null} user - Current effective user (impersonated or real)
 * @property {User | null} realUser - Always the actual authenticated user
 * @property {boolean} loading - True while checking initial session
 * @property {boolean} isImpersonating - True when admin is viewing as another user
 * @property {Function} signIn - Email/password sign in
 * @property {Function} signUp - Create new account
 * @property {Function} signOut - Log out current user
 * @property {Function} signInWithGoogle - OAuth with Google
 * @property {Function} resetPassword - Send password reset email
 *
 * @example
 * const { user, realUser, isImpersonating } = useAuth();
 *
 * if (isImpersonating) {
 *   console.log('Admin', realUser.email, 'viewing as', user.id);
 * }
 */
export function useAuth() {
  const authContext = useAuthContext();
  
  // Try to get impersonation context (may not be available during initial render)
  let impersonationContext: ImpersonationContextType | undefined;
  try {
    impersonationContext = useContext(ImpersonationContext);
  } catch {
    // Impersonation context not available
  }

  const isImpersonating = impersonationContext?.isImpersonating ?? false;
  const impersonatedUser = impersonationContext?.impersonatedUser;

  // When impersonating, create a synthetic user object with the impersonated user's ID
  // This allows existing hooks that use user.id to automatically fetch impersonated data
  let effectiveUser: User | null = authContext.user;
  
  if (isImpersonating && impersonatedUser && authContext.user) {
    // Create a shallow copy with the impersonated user's ID
    effectiveUser = {
      ...authContext.user,
      id: impersonatedUser.id,
      email: impersonatedUser.email,
    };
  }

  return {
    ...authContext,
    user: effectiveUser,
    realUser: authContext.user,
    isImpersonating,
    impersonatedUser,
  };
}
